import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton, NioIcon } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateServiceLabel } from '../../../i18n/translateService';

function formatDate(value, language) {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return '';
  const locale = language === 'en' ? 'en-US' : 'fr-FR';
  return new Date(year, month - 1, day).toLocaleDateString(locale);
}

function RequestDemandStep8() {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();

  const { t, language } = useTranslation();

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const parentServiceTitle = location.state?.parentServiceTitle || t('common.service');
  const projectTitle = location.state?.projectTitle || '';
  const requestDetails = location.state?.requestDetails || {};
  const requestStep4Details = location.state?.requestStep4Details || {};
  const requestStep5Details = location.state?.requestStep5Details || {};
  const requestStep6Details = location.state?.requestStep6Details || {};
  const requestStep7Details = location.state?.requestStep7Details || {};
  const isStep5Skipped = location.state?.isStep5Skipped || false;
  const displayStep = isStep5Skipped ? 7 : 8;

  const [selectedMode, setSelectedMode] = useState('exact-date');
  const [exactDate, setExactDate] = useState('');
  const [exactTime, setExactTime] = useState('');
  const [beforeDate, setBeforeDate] = useState('');
  const [beforeTime, setBeforeTime] = useState('');
  const [openCalendarFor, setOpenCalendarFor] = useState('');

  const canContinue = useMemo(() => {
    return selectedMode === 'exact-date'
      ? Boolean(exactDate && exactTime)
      : Boolean(beforeDate && beforeTime);
  }, [selectedMode, exactDate, exactTime, beforeDate, beforeTime]);

  return (
    <AppLayout title={`${t('wizard.step', { n: displayStep })} - ${t('wizard.step8Title')}`} rootClass="layout-1">
      <style>{`
        .request-step8-page {
          background: #f2f3f5;
          min-height: 78vh;
          border-radius: 14px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .request-step8-content {
          flex: 1 1 auto;
          width: 100%;
          max-width: 760px;
          margin: 0 auto;
          padding-top: 4.2rem;
          padding-bottom: 2rem;
        }
        .request-step8-step {
          color: #697586;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }
        .request-step8-heading {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 0.4rem;
        }
        .request-step8-description {
          color: #5f6b7a;
          margin-bottom: 3rem;
          max-width: 620px;
        }
        .request-step8-label {
          color: #0f2b3d;
          font-size: 1.05rem;
          font-weight: 500;
          margin-bottom: 0.9rem;
        }
        .request-step8-select {
          width: 100%;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          min-height: 56px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 0.85rem;
          color: #0f2b3d;
          margin-bottom: 0.95rem;
          text-align: left;
        }
        .request-step8-select.is-active {
          border-color: #2563eb;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
        }
        .request-step8-select-value {
          font-weight: 500;
        }
        .request-step8-calendar {
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          width: 290px;
          padding: 0.75rem;
          margin-top: -0.4rem;
          margin-bottom: 0.9rem;
          box-shadow: 0 8px 24px rgba(15, 43, 61, 0.08);
        }
        .request-step8-calendar label {
          color: #5f6b7a;
          font-size: 0.86rem;
          margin-bottom: 0.4rem;
          display: block;
        }
        .request-step8-calendar input {
          width: 100%;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          height: 42px;
          padding: 0 0.7rem;
          color: #0f2b3d;
        }
        .request-step8-footer {
          flex-shrink: 0;
          margin-top: auto;
          border-top: 1px solid #dde3ea;
          padding: 0.7rem 1.1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fb;
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          z-index: 5;
          box-shadow: 0 -4px 16px rgba(15, 43, 61, 0.08);
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-4">
        <NioSection.Content>
          <div className="request-step8-page p-3 p-md-4">
            <div className="request-step8-content">
              <div className="request-step8-step">{t('wizard.step', { n: displayStep })}</div>
              <h2 className="request-step8-heading">{t('wizard.step8Heading')}</h2>
              <p className="request-step8-description">{t('wizard.step8Description')}</p>

              <h3 className="request-step8-label">{t('wizard.step8DeadlineLabel')}</h3>

              <button
                type="button"
                className={`request-step8-select ${selectedMode === 'exact-date' ? 'is-active' : ''}`}
                onClick={() => {
                  setSelectedMode('exact-date');
                  setOpenCalendarFor('exact-date');
                }}
              >
                <span className="request-step8-select-value">
                  {exactDate
                    ? t('wizard.step8ExactDateWithDate', {
                        date: formatDate(exactDate, language),
                        time: exactTime ? t('wizard.step8TimePrefix', { time: exactTime }) : '',
                      })
                    : t('wizard.step8ExactDate')}
                </span>
                <NioIcon name="chevron-down" />
              </button>

              {openCalendarFor === 'exact-date' && (
                <div className="request-step8-calendar">
                  <label>{t('wizard.step8ChooseDate')}</label>
                  <input
                    type="date"
                    value={exactDate}
                    onChange={(event) => setExactDate(event.target.value)}
                  />
                  <label className="mt-2">{t('wizard.step8ChooseTime')}</label>
                  <input
                    type="time"
                    value={exactTime}
                    onChange={(event) => setExactTime(event.target.value)}
                  />
                </div>
              )}

              <button
                type="button"
                className={`request-step8-select ${selectedMode === 'before-date' ? 'is-active' : ''}`}
                onClick={() => {
                  setSelectedMode('before-date');
                  setOpenCalendarFor('before-date');
                }}
              >
                <span className="request-step8-select-value">
                  {beforeDate
                    ? t('wizard.step8BeforeDateWithDate', {
                        date: formatDate(beforeDate, language),
                        time: beforeTime ? t('wizard.step8TimePrefix', { time: beforeTime }) : '',
                      })
                    : t('wizard.step8BeforeDatePlain')}
                </span>
                <NioIcon name="chevron-down" />
              </button>

              {openCalendarFor === 'before-date' && (
                <div className="request-step8-calendar">
                  <label>{t('wizard.step8DeadlineDate')}</label>
                  <input
                    type="date"
                    value={beforeDate}
                    onChange={(event) => setBeforeDate(event.target.value)}
                  />
                  <label className="mt-2">{t('wizard.step8DeadlineTime')}</label>
                  <input
                    type="time"
                    value={beforeTime}
                    onChange={(event) => setBeforeTime(event.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="request-step8-footer">
              <NioButton
                className="btn btn-primary"
                label={t('common.back')}
                onClick={() => navigate(-1)}
              />
              <NioButton
                className={`btn ${canContinue ? 'btn-primary' : 'btn-primary disabled'}`}
                label={t('common.next')}
                onClick={() => {
                  if (!canContinue) return;
                  navigate(`/service/${serviceId}/request-demand-step9`, {
                    state: {
                      selectedSubService,
                      parentServiceTitle,
                      projectTitle,
                      isStep5Skipped,
                      requestDetails,
                      requestStep4Details,
                      requestStep5Details,
                      requestStep6Details,
                      requestStep7Details,
                      requestStep8Details: {
                        mode: selectedMode,
                        exactDate,
                        exactTime,
                        beforeDate,
                        beforeTime,
                      },
                    },
                  });
                }}
              />
            </div>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default RequestDemandStep8;
